import type {
  Phien, TrangThaiExtension, DanhBa, Nhom, ChienDich, Job, TrangThaiAI, LichDinhKy,
  BaiDeBinhLuan, BoCauBinhLuan, KetQuaGuiBinhLuan,
  DanhSachMcpSession, TaoMcpSessionInput, TaoMcpSessionResponse,
  DanhSachMcpAudit, DanhSachMcpWriteProposal, McpWriteProposalActionResponse,
  BoSuuTapNhom, KetQuaThamGiaNhom,
} from './kieu';

/**
 * Lớp gọi API.
 *
 * Nguyên tắc: KHÔNG nuốt lỗi. Bản cũ để component tự gọi fetch rồi mỗi nơi xử
 * lý lỗi một kiểu, nên có chỗ hỏng mà giao diện vẫn im lặng. Ở đây mọi lỗi đều
 * ném ra dưới dạng LoiMayChu có mã rõ ràng, nơi gọi tự quyết hiển thị thế nào.
 */

export class LoiMayChu extends Error {
  constructor(
    public trangThai: number,
    public ma: string | null,
    message: string,
    public dulieu?: unknown,
  ) {
    super(message);
    this.name = 'LoiMayChu';
  }
}

/**
 * Hạn giờ cho MỌI lời gọi, kể cả khi máy chủ không đặt hạn giờ của nó.
 *
 * Vì sao cần: đo được ngày 14/09/2026, `/api/fbi/groups` và `/api/fbi/campaigns`
 * treo hơn 20 giây vì tầng dưới đi tải trang Facebook thật. Giao diện kẹt ở
 * khung xương đang tải, người dùng không biết chuyện gì đang xảy ra.
 *
 * Máy chủ đã được sửa, nhưng giao diện vẫn phải có hạn giờ riêng: không bao giờ
 * tin tầng dưới sẽ trả lời đúng hẹn. Thà báo "máy chủ phản hồi chậm" còn hơn
 * treo im lặng.
 */
const HAN_GIO_MAC_DINH_MS = 15000;

async function goi<T>(duongDan: string, tuyChon: RequestInit & { hanGioMs?: number } = {}): Promise<T> {
  const { hanGioMs = HAN_GIO_MAC_DINH_MS, ...conLai } = tuyChon;
  const ngat = new AbortController();
  const hen = setTimeout(() => ngat.abort(), hanGioMs);

  let res: Response;
  try {
    res = await fetch(duongDan, {
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(conLai.headers || {}) },
      signal: ngat.signal,
      ...conLai,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new LoiMayChu(0, 'QUA_HAN',
        `Máy chủ không trả lời sau ${Math.round(hanGioMs / 1000)} giây. Thử lại sau ít phút.`);
    }
    // Mất mạng, DNS hỏng, máy chủ không với tới được.
    throw new LoiMayChu(0, 'MAT_KET_NOI',
      'Không kết nối được máy chủ. Kiểm tra đường truyền rồi thử lại.');
  } finally {
    clearTimeout(hen);
  }

  const kieuND = res.headers.get('content-type') || '';
  let than: unknown = null;
  if (kieuND.includes('application/json')) {
    try { than = await res.json(); } catch { /* thân rỗng hoặc hỏng */ }
  }

  if (!res.ok) {
    const b = (than || {}) as { code?: string; message?: string; data?: unknown };
    throw new LoiMayChu(
      res.status,
      b.code || null,
      b.message || moTaTheoMa(res.status),
      b.data,
    );
  }

  // Máy chủ có thể trả HTTP 200 kèm { ok: false }. Đây là bẫy đã gặp thật:
  // /api/fbi/account/status trả 200 nhưng ok:false + EXTENSION_OFFLINE.
  const b = than as { ok?: boolean; code?: string; message?: string } | null;
  if (b && b.ok === false) {
    throw new LoiMayChu(res.status, b.code || null, b.message || 'Máy chủ từ chối yêu cầu.', than);
  }

  return than as T;
}

function moTaTheoMa(ma: number): string {
  if (ma === 401) return 'Phiên đăng nhập đã hết hạn. Hãy đăng nhập lại.';
  if (ma === 402) return 'Tính năng này cần gói trả phí.';
  if (ma === 403) return 'Bạn không có quyền thực hiện việc này.';
  if (ma === 409) return 'Trạng thái hiện tại không cho phép thao tác này.';
  if (ma === 429) return 'Bạn thao tác hơi nhanh. Chờ một chút rồi thử lại.';
  if (ma >= 500) return `Máy chủ đang bảo trì hoặc phản hồi chậm (Lỗi ${ma}). Vui lòng thử lại sau ít phút.`;
  return `Yêu cầu không thành công (Lỗi ${ma}).`;
}

// ===== Các lời gọi =====

export const api = {
  phien: () => goi<Phien>('/api/session'),
  dangXuat: () => goi<{ ok: boolean }>('/api/logout', { method: 'POST' }),

  trangThaiExtension: () => goi<TrangThaiExtension>('/api/extension/status'),
  danhBa: () => goi<DanhBa>('/api/fbi/account/directory'),

  // Hai lời gọi này từng treo 20+ giây (BUG-6). Máy chủ đã sửa, nhưng vẫn để
  // hạn giờ rộng hơn mặc định vì chúng thật sự có thể phải hỏi xuống Extension.
  nhom: (fbUid?: string) => goi<{ ok: boolean; groups: Nhom[]; reason?: string; message?: string }>(
    '/api/fbi/groups' + (fbUid ? `?fbUid=${encodeURIComponent(fbUid)}` : ''), { hanGioMs: 20000 }),
  // Đồng bộ lại danh sách nhóm là việc nặng: phải cào nhiều trang Facebook.
  dongBoNhom: () => goi<{ ok: boolean }>('/api/fbi/groups?forceSync=true', { hanGioMs: 90000 }),
  timNhom: (query: string, limit = 20) => goi<{ ok: boolean; groups: Nhom[]; total: number; message?: string }>(
    '/api/fbi/groups/search', { method: 'POST', body: JSON.stringify({ query, limit }), hanGioMs: 20000 }),
  thamGiaNhom: (groupIds: string[], idempotencyKey: string) => goi<KetQuaThamGiaNhom>('/api/fbi/groups/join', {
    method: 'POST',
    body: JSON.stringify({ groupIds }),
    headers: { 'Idempotency-Key': idempotencyKey },
    hanGioMs: 30000,
  }),
  boSuuTapNhom: (fbUid?: string) => goi<{ ok: boolean; collections: BoSuuTapNhom[] }>(
    '/api/fbi/group-collections' + (fbUid ? `?uid=${encodeURIComponent(fbUid)}` : '')),
  taoBoSuuTapNhom: (name: string) => goi<{ ok: boolean; collection: BoSuuTapNhom }>('/api/fbi/group-collections', {
    method: 'POST',
    body: JSON.stringify({ action: 'CREATE_COLLECTION', name }),
  }),
  ganNhomVaoBoSuuTap: (fbUid: string, groupIds: string[], collectionName: string) =>
    goi<{ ok: boolean; updatedCount: number; collectionName: string }>('/api/fbi/group-collections', {
      method: 'POST',
      body: JSON.stringify({ action: 'ASSIGN_GROUPS', uid: fbUid, groupIds, collectionName }),
    }),

  chienDich: () => goi<{ ok: boolean; campaigns: ChienDich[]; total: number }>(
    '/api/fbi/campaigns', { hanGioMs: 20000 }),
  congViec: () => goi<{ ok: boolean; jobs: Job[]; total: number }>(
    '/api/fbi/jobs', { hanGioMs: 20000 }),
  congViecTheoChienDich: (id: string) => goi<{ ok: boolean; jobs: Job[]; total: number }>(
    `/api/fbi/campaigns/${encodeURIComponent(id)}/jobs`),

  /**
   * Tạo chiến dịch đăng bài. ĐÂY LÀ THAO TÁC GHI LÊN FACEBOOK THẬT.
   *
   * Máy chủ kiểm tra lại toàn bộ trong `lab-server/schemas/dispatch.schema.js`.
   * Giao diện cũng kiểm trước, nhưng chỉ để báo lỗi sớm cho khách — KHÔNG được
   * coi đó là đủ. Máy chủ mới là nơi quyết định, vì API có thể bị gọi thẳng.
   */
  taoChienDich: (than: Record<string, unknown>) =>
    goi<{ ok: boolean; code?: string; message?: string; data?: { campaignId: string; jobIds: string[]; total: number } }>(
      '/api/fbi/dispatch', { method: 'POST', body: JSON.stringify(than), hanGioMs: 30000 }),
  xoaChienDich: (id: string) =>
    goi<{ ok: boolean }>(`/api/fbi/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  dieuKhienChienDich: (id: string, hanhDong: 'pause' | 'resume' | 'abort') =>
    goi<{ ok: boolean; updated: number }>(
      `/api/fbi/campaigns/${encodeURIComponent(id)}/${hanhDong}`, { method: 'POST' }),
  dieuKhienCongViec: (id: string, hanhDong: 'retry' | 'cancel') =>
    goi<{ ok: boolean; status?: string }>(
      `/api/fbi/jobs/${encodeURIComponent(id)}/${hanhDong}`, { method: 'POST' }),
  chayLaiChienDich: (id: string) =>
    goi<{ ok: boolean; campaignId?: string; jobs?: string[] }>(
      `/api/fbi/campaigns/${encodeURIComponent(id)}/rerun`, { method: 'POST' }),

  lichDinhKy: () => goi<{ ok: boolean; schedules: LichDinhKy[] }>('/api/fbi/recurring'),

  /**
   * Tạo lịch mới. Bốn trường bắt buộc, backend chặn nếu thiếu
   * (recurring.routes.js:46-57): message, targets, times, fbUid.
   */
  taoLichDinhKy: (than: {
    name?: string;
    message: string;
    targets: string[];
    times: string[];
    fbUid: string;
    media?: unknown[];
    isAnonymous?: boolean;
    safeMode?: boolean;
    delayFromSec?: number;
    delayToSec?: number;
    startDate?: string | null;
    endDate?: string | null;
  }) =>
    goi<{ ok: boolean; schedule: LichDinhKy }>('/api/fbi/recurring', {
      method: 'POST',
      body: JSON.stringify(than),
    }),

  /**
   * Bật/tắt lịch. Backend CHỈ nhận ACTIVE hoặc PAUSED
   * (recurring.routes.js:86) — COMPLETED do bộ chạy nền tự đặt khi quá hạn.
   */
  doiTrangThaiLich: (id: string, status: 'ACTIVE' | 'PAUSED') =>
    goi<{ ok: boolean; schedule: LichDinhKy }>(
      `/api/fbi/recurring/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

  xoaLichDinhKy: (id: string) =>
    goi<{ ok: boolean }>(`/api/fbi/recurring/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  trangThaiAI: () => goi<TrangThaiAI>('/api/fbi/ai/status'),
  aiVietLai: (text: string, variants?: number) =>
    goi<{ ok: boolean; text: string }>('/api/fbi/ai/rewrite', {
      method: 'POST',
      body: JSON.stringify({ text, variants }),
    }),

  baiDeBinhLuan: () => goi<{ ok: boolean; posts: BaiDeBinhLuan[]; total: number }>('/api/fbi/comments/postable'),
  guiBinhLuan: (than: {
    targets: string[];
    comments: string[];
    delayFromSec: number;
    delayToSec: number;
    safeMode: boolean;
    fbUid?: string;
  }, idempotencyKey: string) => goi<KetQuaGuiBinhLuan>('/api/fbi/comments/dispatch', {
    method: 'POST',
    body: JSON.stringify(than),
    headers: { 'Idempotency-Key': idempotencyKey },
    hanGioMs: 30000,
  }),
  boCauBinhLuan: () => goi<{ ok: boolean; collections: BoCauBinhLuan[] }>('/api/fbi/comments/collection'),
  luuBoCauBinhLuan: (than: { id?: string; name: string; items: string[] }) =>
    goi<{ ok: boolean; collection: Pick<BoCauBinhLuan, 'id' | 'name' | 'count'> }>('/api/fbi/comments/collection', {
      method: 'POST',
      body: JSON.stringify(than),
    }),

  mcpSessions: () => goi<DanhSachMcpSession>('/api/fbi/mcp-sessions'),
  taoMcpSession: (than: TaoMcpSessionInput) => goi<TaoMcpSessionResponse>('/api/fbi/mcp-sessions', {
    method: 'POST',
    body: JSON.stringify(than),
  }),
  huyMcpSession: (id: string) => goi<{ ok: boolean; revoked: boolean; sessionId: string }>(
    `/api/fbi/mcp-sessions/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),
  mcpAudits: (limit = 100) => goi<DanhSachMcpAudit>(`/api/fbi/mcp-audits?limit=${encodeURIComponent(String(limit))}`),
  mcpWriteProposals: (status = 'PENDING_APPROVAL', limit = 100) =>
    goi<DanhSachMcpWriteProposal>(`/api/fbi/mcp-write-proposals?status=${encodeURIComponent(status)}&limit=${encodeURIComponent(String(limit))}`),
  approveMcpWriteProposal: (id: string, approvalOperationId: string) =>
    goi<McpWriteProposalActionResponse>(`/api/fbi/mcp-write-proposals/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approvalOperationId }),
    }),
  rejectMcpWriteProposal: (id: string, reason?: string) =>
    goi<McpWriteProposalActionResponse>(`/api/fbi/mcp-write-proposals/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    }),
};

export { goi };
